import { PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Flex,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import Layout from 'components/Layout';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchPlatformOrganizations } from 'api/platform';
import { StatusTags } from 'containers/Platform/labels';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';

function readinessTags(readiness = {}) {
  const checks = [
    ['Keys', readiness.has_stripe_keys ? 'success' : 'warning', null],
    ['SMS', readiness.has_sms_client ? 'success' : 'warning', null],
    ['Plan', readiness.has_billing_plan ? 'success' : 'warning', null],
    ['Owner', readiness.has_owner ? 'success' : 'warning', null],
  ];

  // The list never calls Stripe, so card presence is unknown here. Only show the Card
  // tag when a caller passes a readiness payload that actually checked Stripe, to avoid
  // a misleading status. The client detail page remains the source of truth.
  if (readiness.payment_method_checked && readiness.payment_method_required) {
    checks.splice(3, 0, [
      'Card',
      readiness.has_payment_method ? 'success' : 'warning',
      readiness.has_payment_method ? 'Card on file.' : 'No card on file yet.',
    ]);
  }

  return (
    <Space size={4} wrap>
      {checks.map(([label, color, tip]) => {
        const tag = <Tag color={color}>{label}</Tag>;
        return tip
          ? <Tooltip key={label} title={tip}>{tag}</Tooltip>
          : <React.Fragment key={label}>{tag}</React.Fragment>;
      })}
    </Space>
  );
}

function PlatformClientsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ results: [], count: 0 });
  const [page, setPage] = useState(1);
  const [loadError, setLoadError] = useState(null);

  const search = searchParams.get('search') || '';
  const isActive = searchParams.get('is_active');
  const isSuspended = searchParams.get('is_suspended');
  const isCatering = searchParams.get('is_catering');
  const missingBillingPlan = searchParams.get('missing_billing_plan');
  const missingSmsClient = searchParams.get('missing_sms_client');
  const missingPaymentMethod = searchParams.get('missing_payment_method');
  const withOverdue = searchParams.get('with_overdue_unpaid_invoices');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const params = { page, search: search || undefined };
    if (isActive !== null && isActive !== '') params.is_active = isActive;
    if (isSuspended !== null && isSuspended !== '') params.is_suspended = isSuspended;
    if (isCatering !== null && isCatering !== '') params.is_catering = isCatering;
    if (missingBillingPlan) params.missing_billing_plan = missingBillingPlan;
    if (missingSmsClient) params.missing_sms_client = missingSmsClient;
    if (missingPaymentMethod) params.missing_payment_method = missingPaymentMethod;
    if (withOverdue) params.with_overdue_unpaid_invoices = withOverdue;

    fetchPlatformOrganizations(params)
      .then((res) => setData(res))
      .catch((e) => {
        captureException(e);
        setLoadError(getHttpErrorMessage(e));
        setData({ results: [], count: 0 });
      })
      .finally(() => setLoading(false));
  }, [
    page, search, isActive, isSuspended, isCatering,
    missingBillingPlan, missingSmsClient, missingPaymentMethod, withOverdue,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === null || value === '') next.delete(key);
    else next.set(key, value);
    setPage(1);
    setSearchParams(next);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name, row) => (
        <Link to={`/platform/clients/${row.id}`} className="font-medium">
          {name}
        </Link>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      render: (_, row) => <StatusTags org={row} />,
    },
    {
      title: 'Owner',
      dataIndex: 'owner_email',
      ellipsis: true,
      render: (email) => email || '—',
    },
    {
      title: 'Readiness',
      dataIndex: 'readiness',
      render: readinessTags,
    },
  ];

  return (
    <Layout
      header={(
        <Layout.Header
          title="Clients"
          extra={[
            <Button
              key="new-client"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/platform/clients/new')}
            >
              New client
            </Button>,
          ]}
        />
      )}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Flex gap={8} wrap="wrap" align="center">
          <Input.Search
            placeholder="Search by name"
            allowClear
            defaultValue={search}
            onSearch={(v) => updateParam('search', v)}
            style={{ width: 220 }}
          />
          <Select
            allowClear
            placeholder="Active"
            style={{ width: 110 }}
            value={isActive === null || isActive === '' ? undefined : isActive}
            onChange={(v) => updateParam('is_active', v)}
            options={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ]}
          />
          <Select
            allowClear
            placeholder="Suspended"
            style={{ width: 130 }}
            value={isSuspended === null || isSuspended === '' ? undefined : isSuspended}
            onChange={(v) => updateParam('is_suspended', v)}
            options={[
              { value: 'true', label: 'Suspended' },
              { value: 'false', label: 'Not suspended' },
            ]}
          />
          <Select
            allowClear
            placeholder="Catering"
            style={{ width: 120 }}
            value={isCatering === null || isCatering === '' ? undefined : isCatering}
            onChange={(v) => updateParam('is_catering', v)}
            options={[
              { value: 'true', label: 'Catering' },
              { value: 'false', label: 'Non-catering' },
            ]}
          />
        </Flex>

        {loadError && (
          <Alert
            type="error"
            showIcon
            message="Could not load clients"
            description={loadError}
            action={(
              <Button size="small" onClick={load}>
                Retry
              </Button>
            )}
          />
        )}

        {(missingBillingPlan || missingSmsClient || missingPaymentMethod || withOverdue) && (
          <Alert
            type="info"
            showIcon
            message="Filtered from reporting"
            action={(
              <Button size="small" onClick={() => setSearchParams({})}>
                Clear filters
              </Button>
            )}
          />
        )}

        <Table
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={data.results || data}
          pagination={{
            current: page,
            total: data.count,
            pageSize: 10,
            onChange: setPage,
            showSizeChanger: false,
          }}
        />
      </Space>
    </Layout>
  );
}

export default PlatformClientsList;
